import { useEditMode } from '@/contexts/EditModeContext';

/**
 * EditableWrapper - Wraps any element to make it clickable in edit mode
 * When clicked in edit mode, it sends a message to the parent editor
 * 
 * @param {string} elementType - Type of element (hero_slide, service, testimonial, etc.)
 * @param {string} elementId - Unique ID of the element
 * @param {object} elementData - Additional data about the element
 * @param {React.ReactNode} children - The content to wrap
 * @param {string} className - Additional CSS classes
 * @param {string} label - Label to show on hover in edit mode
 */
const EditableWrapper = ({ 
  elementType, 
  elementId, 
  elementData = {}, 
  children, 
  className = '',
  label = 'Click to edit'
}) => {
  const { isEditMode, sendEditMessage } = useEditMode();

  const handleClick = (e) => {
    if (isEditMode) {
      e.preventDefault();
      e.stopPropagation();
      sendEditMessage(elementType, elementId, elementData);
    }
  };

  if (!isEditMode) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div 
      className={`${className} relative group cursor-pointer`}
      onClick={handleClick}
      data-editable="true"
      data-element-type={elementType}
      data-element-id={elementId}
    >
      {/* Edit overlay */}
      <div className="absolute inset-0 border-2 border-dashed border-transparent group-hover:border-blue-500 rounded-lg transition-all duration-200 pointer-events-none z-10">
        <div className="absolute -top-8 left-0 bg-blue-500 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          {label}
        </div>
      </div>
      {children}
    </div>
  );
};

export default EditableWrapper;
